import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Upload, Share2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { EmptyState, SkeletonRows } from "@/components/app/Common";
import { NextActionButton } from "@/components/app/NextActionButton";
import { api, errMsg } from "@/lib/api";
import { SendAssessmentDialog } from "@/pages/assessments/SendAssessmentDialog";
import { JobAssessmentCard } from "@/pages/job/JobAssessmentCard";
import { QuestionBankCard } from "@/pages/job/QuestionBankTab";
import { CredBadge, BarMissingChip } from "@/pages/candidates/CredentialsSection";
import { BulkUpload } from "@/pages/job/BulkUpload";

function BulkBar({ job, sel, rows, clear }) {
  const qc = useQueryClient();
  const [send, setSend] = useState(false);
  const picked = rows.filter((r) => sel.has(r.application_id));
  const canShortlist = picked.filter((r) => r.next.key === "shortlist");
  const canSend = picked.filter((r) => ["shortlist", "send_assessment"].includes(r.next.key));
  const shortlist = async () => {
    try { await api.post("/applications/move", { application_ids: canShortlist.map((r) => r.application_id), stage: "screen_shortlist" }); toast.success(`${canShortlist.length} shortlisted`); qc.invalidateQueries({ queryKey: ["workspace"] }); clear(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!sel.size) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-[6px] border border-brand/20 bg-brand-tint/50 px-4 py-2" role="region" aria-label="Selected candidates" data-testid="bulk-bar">
      <span className="text-sm font-medium">{sel.size} selected</span>
      <Button size="sm" variant="outline" disabled={!canShortlist.length} onClick={shortlist} data-testid="bulk-shortlist">Shortlist ({canShortlist.length})</Button>
      <Button size="sm" disabled={!canSend.length} onClick={() => setSend(true)} data-testid="bulk-shortlist-assess">Shortlist & send assessment ({canSend.length})</Button>
      <Button size="sm" variant="ghost" onClick={clear} aria-label="Clear selection" data-testid="bulk-clear"><X size={14} /></Button>
      {send && <SendAssessmentDialog jobId={job.id} applicationIds={canSend.map((r) => r.application_id)} open onClose={() => { setSend(false); clear(); qc.invalidateQueries({ queryKey: ["workspace"] }); }} onSent={() => {}} />}
    </div>
  );
}

export function WorkspaceStep({ job, data, step, staff }) {
  const nav = useNavigate();
  const [sel, setSel] = useState(new Set());
  const [uploading, setUploading] = useState(false);
  const qc = useQueryClient();
  if (!data) return <SkeletonRows rows={4} />;
  const rows = (data?.rows || []).filter((r) => (step === 1 ? true : r.next.step === step));
  const toggle = (id) => { const n = new Set(sel); n.has(id) ? n.delete(id) : n.add(id); setSel(n); };
  return (
    <div className="space-y-4" data-testid={`workspace-step-${step}`}>
      {step === 1 && staff && (
        <div className="panel flex flex-wrap items-center gap-2 p-4" data-testid="workspace-add">
          <p className="flex-1 text-sm text-ink-secondary">Upload resumes, import a list, or share the careers page.</p>
          <Button size="sm" onClick={() => setUploading(true)} data-testid="workspace-upload"><Upload size={14} /> Upload resumes</Button>
          <Button size="sm" variant="outline" onClick={() => nav("/app/candidates/import")} data-testid="workspace-import"><Share2 size={14} /> Bulk import</Button>
        </div>
      )}
      {uploading && <BulkUpload jobId={job.id} onStarted={() => qc.invalidateQueries({ queryKey: ["workspace"] })} onClose={() => setUploading(false)} />}
      {step === 3 && <JobAssessmentCard job={job} staff={staff} />}
      {step === 4 && <QuestionBankCard job={job} />}
      {staff && step === 2 && <BulkBar job={job} sel={sel} rows={rows} clear={() => setSel(new Set())} />}
      {!rows.length ? <EmptyState testId="workspace-empty" title="Nobody at this step" text="Candidates appear here when they reach this step." /> : (
        <ul className="divide-y divide-line rounded-[6px] border border-line bg-white" data-testid="workspace-rows">
          {rows.map((r) => (
            <li key={r.application_id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`ws-row-${r.application_id}`}>
              {staff && step === 2 && <Checkbox checked={sel.has(r.application_id)} onCheckedChange={() => toggle(r.application_id)} aria-label={`Select ${r.name}`} data-testid={`ws-select-${r.application_id}`} />}
              <button className="min-w-[160px] flex-1 text-left text-sm font-medium hover:underline" onClick={() => nav(`/app/candidates/${r.candidate_id}/overview`)}>{r.name}</button>
              <CredBadge s={r.creds} testId={`ws-cred-badge-${r.application_id}`} />
              {r.bar_missing && <BarMissingChip testId={`ws-bar-missing-${r.application_id}`} />}
              <span className="w-20 text-right font-display text-sm font-semibold tabular-nums" title="Fit Score">{r.fit ?? r.resume_match ?? "—"}</span>
              <span className="hidden max-w-[260px] flex-1 text-xs text-ink-muted md:block">{r.next.why}</span>
              <NextActionButton row={r} canAct={staff} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
